import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { formatIsoTimestamp } from "../../util/dateFormat";
import { CompleteMarkdown } from "../internal/CompleteMarkdown";
import { type LinearCommentsPayload, parseLinearCommentsPayload } from "./linearCommentsSchema";
import { LinearIcon } from "./linearIcon";
import { safeLinearUrl } from "./linearRenderUtils";

interface LinearCommentsRendererProps {
  block: StreamingBlock;
}

export const LinearCommentsRenderer = (props: LinearCommentsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LinearCommentsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLinearCommentsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("linear_comments_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().comments.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <LinearIcon class="size-4 shrink-0 text-primary-text" />
                <span class="shrink-0 font-mono text-xs text-muted-text">{data().issueIdentifier}</span>
                <span class="truncate text-sm font-medium text-primary-text">{data().issueTitle}</span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().comments.length === 1
                    ? rt.t("linear_comments_card.results_one")
                    : rt.t("linear_comments_card.results", { count: String(data().comments.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().comments}>
                  {(comment) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="truncate text-sm font-medium text-primary-text">{comment.author}</span>
                        <span class="shrink-0 text-xs text-muted-text">{formatIsoTimestamp(comment.createdAt)}</span>
                      </div>
                      <div class="mt-1 line-clamp-3">
                        <CompleteMarkdown markdown={comment.body} />
                      </div>
                      <Show when={safeLinearUrl(comment.url)}>
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="mt-1 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                          >
                            {rt.t("linear_comments_card.open_link")}
                            <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                          </a>
                        )}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
